/**
 * Community Audit Label Analysis Component
 *
 * Displays label comparison metrics with visual progress bars
 * showing exact matches, missing labels, additional labels, and both.
 */

import { Shimmer } from '@soteria/design-system-ve';
import { useEffect, useState } from 'react';

import { ProgressBar } from '../../core/components';
import { getLabelCertaintyProgressColor } from '../shared/constants/audit-metric-colors';

import * as styles from './CommunityAuditLabelAnalysis.css';
import * as shimmerStyles from './shared/shimmer.css';

interface LabelAnalysis {
  exactMatches: { count: number; percentage: number };
  missingLabels: { count: number; percentage: number };
  additionalLabels: { count: number; percentage: number };
  bothMissingAndAdditional: { count: number; percentage: number };
}

interface ComprehensiveAnalytics {
  labelAnalysis: LabelAnalysis;
}

interface CommunityAuditLabelAnalysisProps {
  comprehensiveAnalytics?: ComprehensiveAnalytics | null;
  isLoading?: boolean;
}

// Helper function to render label metric with progress bar and animation
function renderLabelMetric(options: {
  label: string;
  analysis: { count: number; percentage: number };
  metricType: 'exactMatches' | 'missingLabels' | 'additionalLabels' | 'bothMissingAndAdditional';
  animate: boolean;
}) {
  const { label, analysis, metricType, animate } = options;
  // Always show percentage, use 0% when no data
  const displayPercentage = analysis.count > 0 ? analysis.percentage : 0;
  const displayText = analysis.count > 0 ? `${analysis.percentage}%` : '0%';

  // Use shared constants for consistent colors
  const color = getLabelCertaintyProgressColor(metricType);

  return (
    <div className={styles.metricItem}>
      <div className={styles.metricHeader}>
        <span className={styles.label}>{label}</span>
        <span className={styles.percentage}>{displayText}</span>
      </div>
      <ProgressBar animated={animate} aria-label={`${label}: ${displayText}`} color={color} size="panel" value={animate ? displayPercentage : 0} />
    </div>
  );
}

export function CommunityAuditLabelAnalysis({ comprehensiveAnalytics, isLoading = false }: CommunityAuditLabelAnalysisProps) {
  const [animate, setAnimate] = useState(false);

  // Trigger animation when data loads
  useEffect(() => {
    if (!isLoading && comprehensiveAnalytics) {
      // Small delay to ensure DOM is ready, then trigger animation
      const timer = setTimeout(() => setAnimate(true), 200); // Slightly longer delay for staggered effect

      return () => clearTimeout(timer);
    } else {
      setAnimate(false);
    }
  }, [isLoading, comprehensiveAnalytics]);

  // Render loading state
  if (isLoading) {
    return (
      <div className={styles.panel}>
        <div className={styles.header}>
          <Shimmer className={shimmerStyles.shimmerHeader} />
        </div>
        <div className={styles.content}>
          {['exact', 'missing', 'extra', 'both'].map(metric => (
            <div key={`shimmer-${metric}`} className={styles.metricItem}>
              <div className={styles.metricHeader}>
                <Shimmer className={shimmerStyles.shimmerLabel} />
                <Shimmer className={shimmerStyles.shimmerSmallValue} />
              </div>
              <Shimmer className={shimmerStyles.shimmerProgressBar} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // If no analytics data, show empty progress bars
  if (!comprehensiveAnalytics) {
    const emptyAnalysis = { count: 0, percentage: 0 };

    return (
      <div className={styles.panel}>
        <div className={styles.header}>
          <span className={styles.title}>Audit Label Certainty</span>
        </div>
        <div className={styles.content}>
          {renderLabelMetric({ label: 'Exact', analysis: emptyAnalysis, metricType: 'exactMatches', animate: false })}
          {renderLabelMetric({ label: 'Missing', analysis: emptyAnalysis, metricType: 'missingLabels', animate: false })}
          {renderLabelMetric({ label: 'Extra', analysis: emptyAnalysis, metricType: 'additionalLabels', animate: false })}
          {renderLabelMetric({ label: 'Both', analysis: emptyAnalysis, metricType: 'bothMissingAndAdditional', animate: false })}
        </div>
      </div>
    );
  }

  const { labelAnalysis } = comprehensiveAnalytics;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.title}>Audit Label Certainty</span>
      </div>
      <div className={styles.content}>
        {renderLabelMetric({ label: 'Exact', analysis: labelAnalysis.exactMatches, metricType: 'exactMatches', animate })}
        {renderLabelMetric({ label: 'Missing', analysis: labelAnalysis.missingLabels, metricType: 'missingLabels', animate })}
        {renderLabelMetric({ label: 'Extra', analysis: labelAnalysis.additionalLabels, metricType: 'additionalLabels', animate })}
        {renderLabelMetric({ label: 'Both', analysis: labelAnalysis.bothMissingAndAdditional, metricType: 'bothMissingAndAdditional', animate })}
      </div>
    </div>
  );
}
